import CryptoJS from 'crypto-js';

// Global pepper from env — never commit this value to GitHub
const PEPPER = process.env.REACT_APP_VAULT_SECRET || 'change-this-pepper';

// Derives a unique AES key per user using their userId + pepper.
// User A and User B will always get different keys even with the same pepper.
const deriveKey = (userId) => {
  return CryptoJS.PBKDF2(userId, PEPPER, {
    keySize: 256 / 32,
    iterations: 1000,
  }).toString();
};

/**
 * Encrypts a plain-text string using a key derived from the userId.
 * Returns the encrypted cipher string to store in Supabase.
 */
export const encrypt = (text, userId) => {
  if (!text || !userId) return text;
  try {
    const key = deriveKey(userId);
    return CryptoJS.AES.encrypt(String(text), key).toString();
  } catch {
    return text;
  }
};

/**
 * Decrypts a cipher string using a key derived from the userId.
 * Returns the original plain-text string for display in the UI.
 */
export const decrypt = (cipher, userId) => {
  if (!cipher || !userId) return cipher;
  try {
    const key = deriveKey(userId);
    const bytes = CryptoJS.AES.decrypt(cipher, key);
    const plain = bytes.toString(CryptoJS.enc.Utf8);
    return plain || cipher; // if decryption yields empty, return as-is (old unencrypted data)
  } catch {
    return cipher; // graceful fallback for old unencrypted rows
  }
};

// Fields in vault_links that must be encrypted before saving / decrypted after loading
export const VAULT_SENSITIVE_FIELDS = [
  'password',
  'sso_password',
  'username',
  'sso_email',
  'otp_value',
];

/**
 * Encrypts all sensitive fields in a vault link object before saving to Supabase.
 */
export const encryptVaultLink = (link, userId) => {
  const out = { ...link };
  VAULT_SENSITIVE_FIELDS.forEach(field => {
    if (out[field]) out[field] = encrypt(out[field], userId);
  });
  return out;
};

/**
 * Decrypts all sensitive fields in a vault link object after loading from Supabase.
 */
export const decryptVaultLink = (link, userId) => {
  const out = { ...link };
  VAULT_SENSITIVE_FIELDS.forEach(field => {
    if (out[field]) out[field] = decrypt(out[field], userId);
  });
  return out;
};
